import React, { useState, useEffect } from "react";
import { personnelApi } from "../../api/personnelApi";

const CONTRACT_TYPES = [
  { value: "FIXED_TERM", label: "A tempo determinato" },
  { value: "PERMANENT", label: "A tempo indeterminato" },
  { value: "SEASONAL", label: "Stagionale" },
  { value: "APPRENTICESHIP", label: "Apprendistato" },
  { value: "OTHER", label: "Altro" },
];

export default function EmployeeEditModal({ show, employee, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fiscalCode: "",
    address: "",
    matricola: "",
    // Anagrafica estesa
    birthDate: "",
    birthPlace: "",
    gender: "",
    levelCode: "",
    qualification: "",
    // Contratto
    contractType: "",
    contractStartDate: "",
    contractEndDate: "",
    contractNotes: "",
    // Ferie
    accruedDays: "",
    usedDays: "",
    // Permessi
    permessiDays: "",
  });
  const [contractId, setContractId] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && employee) {
      setForm({
        firstName: employee.firstName ?? "",
        lastName: employee.lastName ?? "",
        email: employee.email ?? "",
        phone: employee.phone ?? "",
        fiscalCode: employee.fiscalCode ?? "",
        address: employee.address ?? "",
        matricola: employee.matricola ?? "",
        birthDate: employee.birthDate ? String(employee.birthDate).slice(0, 10) : "",
        birthPlace: employee.birthPlace ?? "",
        gender: employee.gender ?? "",
        levelCode: employee.levelCode ?? "",
        qualification: employee.qualification ?? "",
        contractType: "",
        contractStartDate: "",
        contractEndDate: "",
        contractNotes: "",
        accruedDays: "",
        usedDays: "",
        permessiDays: "",
      });
      setContractId(null);
      loadContractsAndLeave();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadContractsAndLeave/employee gestiti internamente
  }, [show, employee?.id]);

  async function loadContractsAndLeave() {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const [contracts, leave] = await Promise.all([
        personnelApi.listContracts(employee.id),
        personnelApi.getLeave(employee.id),
      ]);
      const c = Array.isArray(contracts) ? contracts[0] : contracts;
      if (c) {
        setContractId(c.id);
        setForm((prev) => ({
          ...prev,
          contractType: c.type ?? "",
          contractStartDate: c.startDate ? c.startDate.slice(0, 10) : "",
          contractEndDate: c.endDate ? c.endDate.slice(0, 10) : "",
          contractNotes: c.notes ?? "",
        }));
      }
      if (leave) {
        setForm((prev) => ({
          ...prev,
          accruedDays: String(leave.accruedDays ?? 0),
          usedDays: String(leave.usedDays ?? 0),
          permessiDays: String(leave.permessiDays ?? 0),
        }));
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  if (!show || !employee) return null;

  async function save() {
    setErr("");
    setSaving(true);
    try {
      await personnelApi.updateEmployee(employee.id, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email || undefined,
        phone: form.phone || undefined,
        fiscalCode: form.fiscalCode || undefined,
        address: form.address || undefined,
        matricola: form.matricola || undefined,
        birthDate: form.birthDate || undefined,
        birthPlace: form.birthPlace || undefined,
        gender: form.gender || undefined,
        levelCode: form.levelCode || undefined,
        qualification: form.qualification || undefined,
      });

      if (form.contractType && form.contractStartDate) {
        const contractDto = {
          type: form.contractType,
          startDate: form.contractStartDate,
          endDate: form.contractEndDate || undefined,
          notes: form.contractNotes || undefined,
        };
        if (contractId) {
          await personnelApi.updateContract(contractId, contractDto);
        } else {
          await personnelApi.createContract(employee.id, contractDto);
        }
      }

      const accrued = parseFloat(form.accruedDays) || 0;
      const used = parseFloat(form.usedDays) || 0;
      const permessi = parseFloat(form.permessiDays) || 0;
      await personnelApi.upsertLeave(employee.id, {
        accruedDays: accrued,
        usedDays: used,
        permessiDays: permessi,
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      role="dialog"
      style={{ background: "rgba(0,0,0,0.35)" }}
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Modifica dipendente</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loading && (
              <div className="text-muted small mb-3">
                <span className="spinner-border spinner-border-sm me-1" />
                Caricamento…
              </div>
            )}

            <h6 className="border-bottom pb-1 mb-2">Dati anagrafici</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label">Nome</label>
                <input
                  className="form-control"
                  value={form.firstName}
                  onChange={(e) =>
                    setForm({ ...form, firstName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Cognome</label>
                <input
                  className="form-control"
                  value={form.lastName}
                  onChange={(e) =>
                    setForm({ ...form, lastName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  className="form-control"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Telefono</label>
                <input
                  className="form-control"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Codice Fiscale</label>
                <input
                  className="form-control"
                  value={form.fiscalCode}
                  onChange={(e) =>
                    setForm({ ...form, fiscalCode: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="form-label">Indirizzo</label>
                <input
                  className="form-control"
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Matricola</label>
                <input
                  className="form-control"
                  value={form.matricola}
                  onChange={(e) =>
                    setForm({ ...form, matricola: e.target.value })
                  }
                  placeholder="Codice interno dipendente"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Comune di nascita</label>
                <input
                  className="form-control"
                  value={form.birthPlace}
                  onChange={(e) =>
                    setForm({ ...form, birthPlace: e.target.value })
                  }
                  placeholder="Comune (es. Verona)"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Data di nascita</label>
                <input
                  className="form-control"
                  type="date"
                  value={form.birthDate}
                  onChange={(e) =>
                    setForm({ ...form, birthDate: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Genere</label>
                <select
                  className="form-select"
                  value={form.gender}
                  onChange={(e) =>
                    setForm({ ...form, gender: e.target.value })
                  }
                >
                  <option value="">—</option>
                  <option value="MALE">Maschio</option>
                  <option value="FEMALE">Femmina</option>
                  <option value="OTHER">Altro / Non specificato</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Codice livello</label>
                <input
                  className="form-control"
                  value={form.levelCode}
                  onChange={(e) =>
                    setForm({ ...form, levelCode: e.target.value })
                  }
                  placeholder="Es. 4S, 5, 6"
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Qualifica</label>
                <input
                  className="form-control"
                  value={form.qualification}
                  onChange={(e) =>
                    setForm({ ...form, qualification: e.target.value })
                  }
                  placeholder="Es. Cuoco, Cameriere, Responsabile di sala"
                />
              </div>
            </div>

            <h6 className="border-bottom pb-1 mb-2">Contratto</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label">Tipo</label>
                <select
                  className="form-select"
                  value={form.contractType}
                  onChange={(e) =>
                    setForm({ ...form, contractType: e.target.value })
                  }
                >
                  <option value="">—</option>
                  {CONTRACT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Data inizio</label>
                <input
                  className="form-control"
                  type="date"
                  value={form.contractStartDate}
                  onChange={(e) =>
                    setForm({ ...form, contractStartDate: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Data fine</label>
                <input
                  className="form-control"
                  type="date"
                  value={form.contractEndDate}
                  onChange={(e) =>
                    setForm({ ...form, contractEndDate: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="form-label">Note contratto</label>
                <input
                  className="form-control"
                  value={form.contractNotes}
                  onChange={(e) =>
                    setForm({ ...form, contractNotes: e.target.value })
                  }
                  placeholder="Opzionale"
                />
              </div>
            </div>

            <h6 className="border-bottom pb-1 mb-2">Ferie</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label">Ferie maturate (gg)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.accruedDays}
                  onChange={(e) =>
                    setForm({ ...form, accruedDays: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Ferie usate (gg)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.usedDays}
                  onChange={(e) =>
                    setForm({ ...form, usedDays: e.target.value })
                  }
                />
              </div>
            </div>

            <h6 className="border-bottom pb-1 mb-2">Permessi</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label">Permessi retribuiti (gg)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.permessiDays}
                  onChange={(e) =>
                    setForm({ ...form, permessiDays: e.target.value })
                  }
                />
              </div>
            </div>

            {err ? (
              <div className="alert alert-danger mt-3 mb-0">{err}</div>
            ) : null}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Annulla
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
